import { useState } from "react";
import { AxiosPromise } from "axios";
import { Download, FileText, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { PagedResponse } from "@/types/PagedResponse";
import { Button } from "../ui/button";
import { Column } from "./DataTable";
import { exportarCsv } from "./exportarCsv";
import { exportarPdf } from "./exportarPdf";
import { parseFilterUrl } from "./buildFilterUrl";
import { useLocation } from "react-router-dom";
import { useMarca } from "../providers/MarcaProvider";

// ponytail: limite de 10 mil linhas por exportação; paginar as requisições se alguma corretora passar disso
export const TODOS_REGISTROS = { page: 0, size: 10000 };

type ExportarButtonProps = {
  arquivo: string;
  columns: Column[];
  buscar(): AxiosPromise<PagedResponse<any>>;
}

const useExportar = (buscar: ExportarButtonProps['buscar'], gerar: (itens: any[]) => void) => {
  const [exportando, setExportando] = useState(false);
  const exportar = () => {
    setExportando(true);
    buscar()
      .then(({ data }) => gerar(data.content))
      .catch(() => toast.error('Não foi possível exportar.'))
      .finally(() => setExportando(false));
  }
  return [exportando, exportar] as const;
}

// Exporta para CSV todos os registros do filtro atual, não só a página visível
export const ExportarButton = ({ arquivo, columns, buscar }: ExportarButtonProps) => {
  const [exportando, exportar] = useExportar(buscar, itens => exportarCsv(arquivo, columns, itens));
  return (
    <Button variant="outline" onClick={exportar} disabled={exportando}>
      {exportando ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Exportar
    </Button>
  );
}

// PDF com todos os registros do filtro atual; o cabeçalho traz a corretora e os filtros da URL
export const ExportarPdfButton = ({ arquivo, titulo, columns, buscar }: ExportarButtonProps & { titulo: string }) => {
  const location = useLocation();
  const { empresa } = useMarca();
  const [gerando, gerar] = useExportar(buscar, itens =>
    exportarPdf(arquivo, titulo, columns, itens, Object.values(parseFilterUrl(columns, location)), empresa));
  return (
    <Button variant="outline" onClick={gerar} disabled={gerando}>
      {gerando ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} PDF
    </Button>
  );
}
